import { ChevronDown, ChevronUp, ListTodo, Pencil, Plus, Save, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";
import { useState } from "react";
import { useConfirm, useToast } from "@/components/shared";
import { teamService } from "@/lib/services";
import type { Agent, TeamHubTaskTemplateSummary, TeamHub as TeamHubType } from "@/types";
import { ColorPicker, FormField, FormInput, FormTextarea, IconPicker } from "./FormWidgets";
import { resolveIcon } from "./utils";

// ---------------------------------------------------------------------------
// Task Template form data
// ---------------------------------------------------------------------------

export interface TaskTemplateFormData {
  name: string;
  description: string;
  icon: string;
  color: string;
  agentUuid: string;
  promptTemplate: string;
  inputPlaceholder: string;
  isEnabled: boolean;
}

export const emptyTaskTemplate: TaskTemplateFormData = {
  name: "",
  description: "",
  icon: "list-todo",
  color: "text-blue-400",
  agentUuid: "",
  promptTemplate: "",
  inputPlaceholder: "",
  isEnabled: true,
};

// ---------------------------------------------------------------------------
// TaskTemplateForm
// ---------------------------------------------------------------------------

export function TaskTemplateForm({
  data,
  onChange,
  agents,
}: Readonly<{
  data: TaskTemplateFormData;
  onChange: (d: TaskTemplateFormData) => void;
  agents: Agent[];
}>) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Name *">
          <FormInput
            value={data.name}
            onChange={(v) => onChange({ ...data, name: v })}
            placeholder="e.g., Company Research"
            maxLength={255}
          />
        </FormField>
        <FormField label="Agent">
          <select
            value={data.agentUuid}
            onChange={(e) => onChange({ ...data, agentUuid: e.target.value })}
            className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-sm text-content-primary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
          >
            <option value="">None (generic chat)</option>
            {agents.map((a) => (
              <option key={a.uuid} value={a.uuid}>
                {a.name}
              </option>
            ))}
          </select>
        </FormField>
      </div>
      <FormField label="Description">
        <FormTextarea
          value={data.description}
          onChange={(v) => onChange({ ...data, description: v })}
          placeholder="What this task template does..."
        />
      </FormField>
      <FormField label="Prompt Template">
        <FormTextarea
          value={data.promptTemplate}
          onChange={(v) => onChange({ ...data, promptTemplate: v })}
          placeholder="e.g., Research the company {{input}} and provide a summary..."
          rows={3}
        />
        <p className="text-[11px] text-content-quaternary mt-1">Use {"{{input}}"} as a placeholder for user input.</p>
      </FormField>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Input Placeholder">
          <FormInput
            value={data.inputPlaceholder}
            onChange={(v) => onChange({ ...data, inputPlaceholder: v })}
            placeholder="e.g., Enter company name..."
          />
        </FormField>
        <FormField label="Icon">
          <IconPicker value={data.icon} onChange={(v) => onChange({ ...data, icon: v })} />
        </FormField>
        <FormField label="Color">
          <ColorPicker value={data.color} onChange={(v) => onChange({ ...data, color: v })} />
        </FormField>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TaskTemplatesSection
// ---------------------------------------------------------------------------

export function TaskTemplatesSection({
  hub,
  agents,
  onRefresh,
}: Readonly<{
  hub: TeamHubType;
  agents: Agent[];
  onRefresh: () => void;
}>) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [expanded, setExpanded] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingUuid, setEditingUuid] = useState<string | null>(null);
  const [formData, setFormData] = useState<TaskTemplateFormData>(emptyTaskTemplate);
  const [saving, setSaving] = useState(false);
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null);
  const [reordering, setReordering] = useState(false);

  const templates = hub.taskTemplates ?? [];

  const handleCreate = async () => {
    if (!formData.name.trim()) return;
    setSaving(true);
    try {
      await teamService.createTaskTemplate(hub.uuid, {
        name: formData.name,
        description: formData.description || undefined,
        icon: formData.icon,
        color: formData.color,
        agentUuid: formData.agentUuid || undefined,
        promptTemplate: formData.promptTemplate || undefined,
        inputPlaceholder: formData.inputPlaceholder || undefined,
        displayOrder: templates.length,
        isEnabled: formData.isEnabled,
      });
      setCreating(false);
      setFormData(emptyTaskTemplate);
      onRefresh();
    } catch (e) {
      console.error("Failed to create task template:", e);
      showToast("Failed to create task template", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingUuid || !formData.name.trim()) return;
    setSaving(true);
    try {
      await teamService.updateTaskTemplate(hub.uuid, editingUuid, {
        name: formData.name,
        description: formData.description || undefined,
        icon: formData.icon,
        color: formData.color,
        agentUuid: formData.agentUuid || undefined,
        promptTemplate: formData.promptTemplate || undefined,
        inputPlaceholder: formData.inputPlaceholder || undefined,
        isEnabled: formData.isEnabled,
      });
      setEditingUuid(null);
      setFormData(emptyTaskTemplate);
      onRefresh();
    } catch (e) {
      console.error("Failed to update task template:", e);
      showToast("Failed to update task template", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (uuid: string) => {
    const confirmed = await confirm({
      title: "Delete Task Template",
      message: "Are you sure you want to delete this task template?",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    setDeletingUuid(uuid);
    try {
      await teamService.deleteTaskTemplate(hub.uuid, uuid);
      onRefresh();
    } catch (e) {
      console.error("Failed to delete task template:", e);
      showToast("Failed to delete task template", "error");
    } finally {
      setDeletingUuid(null);
    }
  };

  const handleToggle = async (template: TeamHubTaskTemplateSummary) => {
    try {
      await teamService.updateTaskTemplate(hub.uuid, template.uuid, {
        isEnabled: !template.isEnabled,
      });
      onRefresh();
    } catch (e) {
      console.error("Failed to toggle task template:", e);
      showToast("Failed to toggle task template", "error");
    }
  };

  const startEdit = (template: TeamHubTaskTemplateSummary) => {
    setCreating(false);
    setEditingUuid(template.uuid);
    setFormData({
      name: template.name,
      description: template.description || "",
      icon: template.icon,
      color: template.color,
      agentUuid: template.agentUuid || "",
      promptTemplate: template.promptTemplate || "",
      inputPlaceholder: template.inputPlaceholder || "",
      isEnabled: template.isEnabled,
    });
  };

  const handleReorder = async (uuid: string, direction: "up" | "down") => {
    if (reordering) return;
    const idx = templates.findIndex((t) => t.uuid === uuid);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= templates.length) return;
    setReordering(true);
    try {
      await Promise.all([
        teamService.updateTaskTemplate(hub.uuid, templates[idx].uuid, {
          displayOrder: templates[swapIdx].displayOrder,
        }),
        teamService.updateTaskTemplate(hub.uuid, templates[swapIdx].uuid, {
          displayOrder: templates[idx].displayOrder,
        }),
      ]);
      onRefresh();
    } catch (e) {
      console.error("Failed to reorder:", e);
      showToast("Failed to reorder", "error");
    } finally {
      setReordering(false);
    }
  };

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-5 hover:bg-interactive-hover transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <ListTodo className="h-5 w-5 text-purple-400" />
          </div>
          <div className="text-left">
            <h3 className="text-sm font-semibold text-content-primary">Task Templates</h3>
            <p className="text-xs text-content-tertiary">
              {templates.length} {templates.length === 1 ? "template" : "templates"} configured
            </p>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-4 w-4 text-content-quaternary" />
        ) : (
          <ChevronDown className="h-4 w-4 text-content-quaternary" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-border-primary">
          {templates.length > 0 && (
            <div className="divide-y divide-border-primary">
              {templates.map((template, idx) => {
                const Icon = resolveIcon(template.icon);
                const isEditing = editingUuid === template.uuid;

                if (isEditing) {
                  return (
                    <div key={template.uuid} className="p-5 bg-surface-secondary/30">
                      <TaskTemplateForm data={formData} onChange={setFormData} agents={agents} />
                      <div className="flex items-center gap-2 mt-4">
                        <button
                          onClick={handleUpdate}
                          disabled={saving || !formData.name.trim()}
                          className="px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center gap-1.5"
                        >
                          <Save className="h-3.5 w-3.5" />
                          {saving ? "Saving..." : "Save Changes"}
                        </button>
                        <button
                          onClick={() => {
                            setEditingUuid(null);
                            setFormData(emptyTaskTemplate);
                          }}
                          className="px-4 py-2 bg-surface-secondary text-content-secondary rounded-lg text-sm font-medium hover:bg-interactive-active transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  );
                }

                return (
                  <div
                    key={template.uuid}
                    className={`flex items-center gap-4 px-5 py-3.5 group ${template.isEnabled ? "" : "opacity-50"}`}
                  >
                    <div className="flex flex-col gap-0.5">
                      <button
                        onClick={() => handleReorder(template.uuid, "up")}
                        disabled={idx === 0}
                        className="p-0.5 text-content-quaternary hover:text-content-secondary disabled:opacity-20 transition-colors"
                      >
                        <ChevronUp className="h-3 w-3" />
                      </button>
                      <button
                        onClick={() => handleReorder(template.uuid, "down")}
                        disabled={idx === templates.length - 1}
                        className="p-0.5 text-content-quaternary hover:text-content-secondary disabled:opacity-20 transition-colors"
                      >
                        <ChevronDown className="h-3 w-3" />
                      </button>
                    </div>
                    <div className="h-9 w-9 rounded-lg bg-surface-secondary flex items-center justify-center shrink-0">
                      <Icon className={`h-4 w-4 ${template.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-content-primary truncate">{template.name}</p>
                        {template.agentName && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-surface-secondary text-content-quaternary">
                            {template.agentName}
                          </span>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-xs text-content-tertiary truncate mt-0.5">{template.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleToggle(template)}
                        title={template.isEnabled ? "Disable" : "Enable"}
                        className="p-1.5 rounded-md hover:bg-interactive-hover transition-colors"
                      >
                        {template.isEnabled ? (
                          <ToggleRight className="h-4 w-4 text-emerald-400" />
                        ) : (
                          <ToggleLeft className="h-4 w-4 text-content-quaternary" />
                        )}
                      </button>
                      <button
                        onClick={() => startEdit(template)}
                        className="p-1.5 rounded-md hover:bg-interactive-hover transition-colors text-content-tertiary hover:text-content-primary"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDelete(template.uuid)}
                        disabled={deletingUuid === template.uuid}
                        className="p-1.5 rounded-md hover:bg-red-500/10 transition-colors text-content-tertiary hover:text-red-400"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {creating ? (
            <div className="p-5 border-t border-border-primary bg-surface-secondary/20">
              <TaskTemplateForm data={formData} onChange={setFormData} agents={agents} />
              <div className="flex items-center gap-2 mt-4">
                <button
                  onClick={handleCreate}
                  disabled={saving || !formData.name.trim()}
                  className="px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 flex items-center gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  {saving ? "Creating..." : "Create Template"}
                </button>
                <button
                  onClick={() => {
                    setCreating(false);
                    setFormData(emptyTaskTemplate);
                  }}
                  className="px-4 py-2 bg-surface-secondary text-content-secondary rounded-lg text-sm font-medium hover:bg-interactive-active transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4 border-t border-border-primary">
              <button
                onClick={() => {
                  setEditingUuid(null);
                  setCreating(true);
                  setFormData(emptyTaskTemplate);
                }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-brand-primary hover:bg-brand-primary/10 rounded-lg transition-colors font-medium"
              >
                <Plus className="h-4 w-4" />
                Add Task Template
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
